import _, { isEmpty } from 'underscore';

import {
  timeToString,
  businessDays,
  tryParseTimeString,
  isHourValid,
  isMinuteValid
} from '@bingads-webui-campaign-react/smart-component-util';

export const getDefaultDayHours = isHoliday => (
  isHoliday ? [] : [{
    index: 0,
    openHour: 9,
    openMinute: 0,
    closeHour: 17,
    closeMinute: 0,
  }]
);

export const getDefaultHoursConfig = () => {
  const days = _.keys(businessDays);
  return _.map(days, (day) => {
    const isHoliday = (day === 'sat' || day === 'sun' || day === 'holiday');
    return {
      day,
      isOpenAllDay: false,
      timeSlots: getDefaultDayHours(isHoliday),
      isValid: true,
    };
  });
};

export const getDayName = (day, i18n) => i18n.getString(businessDays[day]);

export const validateOpenCloseTime = (open, close, crossValidation) => {
  if (open && close) {
    const { hour: openHour, minute: openMinute } = open;
    const { hour: closeHour, minute: closeMinute } = close;

    const validationNumber = _.every(
      [openHour, openMinute, closeHour, closeMinute],
      time => _.isNumber(time)
    );
    const validationDay = crossValidation
      ? (openHour < closeHour) || (openHour === closeHour && openMinute <= closeMinute)
      : (openHour < closeHour) || (openHour === closeHour && openMinute < closeMinute);

    if (validationNumber && validationDay) {
      return true;
    }
  }
  return false;
};

export const getTimeSpanString = (timeSlot, i18n) => {
  if (!timeSlot || timeSlot.openHour === null || timeSlot.openMinute === null ||
    timeSlot.closeHour === null || timeSlot.closeMinute === null) {
    return '-';
  }
  // All day: 12:00 AM - 11.59 PM
  if (timeSlot && timeSlot.openHour === 0 && timeSlot.openMinute === 0
    && (timeSlot.closeHour === 23 && timeSlot.closeMinute === 59)) {
    return i18n.getString(_TL_('All day'));
  }

  const openStr = timeToString({ hour: timeSlot.openHour, minute: timeSlot.openMinute });
  const closeStr = timeToString({ hour: timeSlot.closeHour, minute: timeSlot.closeMinute });

  return `${openStr} - ${closeStr}`;
};

export const getNextHour = hour => (hour + 1 >= 23 ? 23 : hour + 1);

export const sortTimeSlots = (timeSlots = []) => [...timeSlots].sort((a, b) => {
  let compare = a.openHour - b.openHour;
  if (compare === 0) {
    compare = a.openMinute - b.openMinute;
  }
  return compare;
});

export const validateTimeSlots = (timeSlots) => {
  if (_.isEmpty(timeSlots)) {
    return true;
  }

  const sortedArr = sortTimeSlots(timeSlots);
  const isAllValid = sortedArr.every((e, index, sorted) => {
    // validate currrent timeslot;
    let isValid = validateOpenCloseTime(
      { hour: e.openHour, minute: e.openMinute },
      { hour: e.closeHour, minute: e.closeMinute }
    );

    if (index !== 0) {
      // validate adjacent timeslots;
      isValid = validateOpenCloseTime(
        {
          hour: sorted[index - 1].closeHour,
          minute: sorted[index - 1].closeMinute,
        },
        { hour: e.openHour, minute: e.openMinute },
        true
      );
    }

    return isValid;
  });

  return isAllValid;
};

export const reIndexTimeSlots = timeSlots => (
  _.map(timeSlots, (value, index) => {
    if (value.index === undefined) {
      return {
        ...value,
        index,
      };
    }
    return value;
  })
);

/**
 * Get time options with timeslot equals 15min
 * @returns {Array} New time options
 */
export const getTimeOptions = () => {
  const timeArr = Array.from(Array(12), (v, k) => k);
  timeArr[0] = 12;
  const timeslotsStr = ['00', '15', '30', '45'];

  const timeOptionsFactory = (suffix) => {
    const newTimeOptions = [];
    _.forEach(timeArr, (time) => {
      _.forEach(timeslotsStr, (slot) => {
        const timeStr = `${time}:${slot} ${suffix}`;
        newTimeOptions.push({ key: timeStr, text: timeStr });
      });
    });
    return newTimeOptions;
  };

  return [...timeOptionsFactory('AM'), ...timeOptionsFactory('PM')];
};

export const getTimestamp = (hour, minute) => {
  if (!_.isNumber(hour) || !_.isNumber(minute)) {
    return null;
  }

  return (hour * 60) + minute;
};

/**
 * Insert a new option into right place
 * @param {Array} options Time options
 * @param {Object} newOption New time option
 * @returns {Array} New time options
 */
export const insertTimeOption = (options, newOption) => {
  const newOptions = [...options];
  const { hour, minute } = tryParseTimeString(newOption.text) || {};

  if (!_.isNumber(hour) || !_.isNumber(minute)) {
    return options;
  }

  let isExsiting = false;
  const newTimestamp = getTimestamp(hour, minute);

  const targetIndex = _.findIndex(options, (option) => {
    const parsedTime = tryParseTimeString(option.text);

    if (parsedTime) {
      const { hour: optionHour, minute: optionMinute } = parsedTime;
      const optionTimestamp = getTimestamp(optionHour, optionMinute);

      if (newTimestamp === optionTimestamp) {
        isExsiting = true;
      }

      return optionTimestamp > newTimestamp;
    }

    return false;
  });

  if (isExsiting) {
    return options;
  }

  const isValidOption = isHourValid(hour) && isMinuteValid(minute);
  const formattedTimeStr = timeToString({ hour, minute }, 'h:mm');
  const formattedTimeOption = { key: formattedTimeStr, text: formattedTimeStr };

  if (targetIndex !== -1) {
    newOptions.splice(targetIndex, 0, formattedTimeOption);
  } else if (isValidOption) {
    // New option is the last time option
    newOptions.push(formattedTimeOption);
  }

  return newOptions;
};

/**
 * Set part of time options as diabled.
 * @param {Array} options Time options.
 * @param {Object} startOption Start of disabled option
 * @param {Object} endOption End of disabled option
 * @param {boolean} isNegative  If needs to do the negative transformation.
 * @returns {Array} New time options with disabled value.
 */
export const getTimeOptionsWithDisabledValue = (options, startOption, endOption, isNegative) => {
  const startIndex = startOption
    ? _.findIndex(options, option => option.text === startOption.text)
    : 0;
  const endIndex = endOption
    ? _.findIndex(options, option => option.text === endOption.text)
    : options.length - 1;

  if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
    return options;
  }

  if (isNegative) {
    return _.map(options, (option, index) => {
      if (index >= startIndex && index <= endIndex) {
        return { key: option.key, text: option.text };
      }
      return { ...option, disabled: true };
    });
  }

  return _.map(options, (option, index) => {
    if (index >= startIndex && index <= endIndex) {
      return { ...option, disabled: true };
    }
    return { key: option.key, text: option.text };
  });
};

/**
 * Judge if certain time is before or after than another time
 * @param {Object} time Judge target
 * @param {Object} comparedTime Time to be compared
 * @param {Boolean} beforeThanTarget If the time needs to be before than the comparedTime
 * @returns {Boolean} If the time is before or after than another time
 */
export const isTimeOptionValid = (time, comparedTime, beforeThanTarget) => {
  if (isEmpty(time) || isEmpty(comparedTime)) {
    return false;
  }

  const timestamp = getTimestamp(time.hour, time.minute);
  const comparedTimestamp = getTimestamp(comparedTime.hour, comparedTime.minute);

  return beforeThanTarget ? (timestamp < comparedTimestamp) : (timestamp > comparedTimestamp);
};
